import type { FC } from "react";
import { ReviewsRow } from "@/components/facility/ReviewsRow";
import {
    formatPhoneNumber,
    sanitizePhoneNumber,
} from "@/utils/phone/phoneUtils";

interface NearbyFacilityItemProps {
    name: string;
    distance: number;
    address1: string;
    address2: string | null;
    city: string;
    state: string;
    zip: string;
    facilityUrl: string;
    imageUrl?: string;
    phone?: string | null;
    reviewsScore?: number;
    reviewsCount?: number;
    unitTypeName?: string;
    pinNumber?: number;
}

const BadgePin = ({ number }: { number: number }) => (
    <span className="nearby-facility-item__badge-pin tw:relative tw:inline-flex tw:items-center tw:justify-center tw:w-3.5 tw:h-4">
        <svg className="tw:w-3.5 tw:h-4" viewBox="0 0 24 32" aria-hidden="true">
            <path
                d="M12,0C5.4,0,0,5.4,0,12c0,7.2,12,20,12,20s12-12.8,12-20C24,5.4,18.6,0,12,0z"
                fill="#ffffff"
            />
        </svg>
        <span className="tw:absolute tw:top-[1px] tw:text-primary tw:text-[8px] tw:font-bold tw:leading-none">
            {number}
        </span>
    </span>
);

export const NearbyFacilityItem: FC<NearbyFacilityItemProps> = ({
    name,
    distance,
    address1,
    address2,
    city,
    state,
    zip,
    facilityUrl,
    imageUrl,
    phone,
    reviewsScore,
    reviewsCount,
    unitTypeName = "",
    pinNumber,
}) => {
    const handleViewUnits = () => {
        if (!facilityUrl) {
            return;
        }
        const params = new URLSearchParams({ unit_type_name: unitTypeName });
        window.location.href = `${facilityUrl}?${params.toString()}`;
    };

    const streetAddress = address2 ? `${address1}, ${address2}` : address1;
    const cityStateZip = `${city}, ${state} ${zip}`;

    return (
        <div className="nearby-facility-item tw:flex tw:flex-col tw:bg-white tw:rounded-lg tw:overflow-hidden tw:shadow-sm tw:border tw:border-gray-200">
            <div className="nearby-facility-item__image-wrap tw:relative tw:w-full tw:h-[160px] tw:bg-gray-200">
                {imageUrl && (
                    <img
                        src={imageUrl}
                        alt={`${name} facility`}
                        className="nearby-facility-item__image tw:w-full tw:h-full tw:object-cover tw:rounded-t-lg tw:rounded-b-none"
                        loading="lazy"
                    />
                )}
                <span className="nearby-facility-item__distance-badge tw:absolute tw:top-3 tw:right-3 tw:inline-flex tw:items-center tw:gap-1.5 tw:bg-primary tw:text-primary-foreground tw:text-xs tw:font-semibold tw:px-2.5 tw:py-1 tw:rounded-full tw:shadow">
                    {pinNumber && <BadgePin number={pinNumber} />}
                    {distance.toFixed(2)} Miles Away
                </span>
            </div>
            <div className="nearby-facility-item__info tw:flex tw:flex-col tw:p-4 tw:flex-1 tw:text-center tw:[&_h3]:m-0 tw:[&_p]:m-0">
                <div className="nearby-facility-item__info-content tw:flex tw:flex-col tw:gap-1 tw:flex-1">
                    <h3 className="nearby-facility-item__name tw:font-bold">
                        {name}
                    </h3>
                    {/* ReviewsRow gates itself (numeric score + count > 0). */}
                    <ReviewsRow
                        score={reviewsScore}
                        count={reviewsCount}
                        url={facilityUrl}
                        classPrefix="nearby-facility-item"
                        ariaLabel={`Reviews for ${name}`}
                        center
                    />
                    <div className="nearby-facility-item__address tw:flex tw:flex-col tw:text-sm tw:text-gray-600">
                        <div className="nearby-facility-item__address-lines">
                            <p className="nearby-facility-item__street-address">
                                {streetAddress}
                            </p>
                            <p className="nearby-facility-item__city-state-zip">
                                {cityStateZip}
                            </p>
                            {phone && (
                                <p className="nearby-facility-item__phone tw:mt-1 tw:text-sm tw:text-gray-600">
                                    <a
                                        href={`tel:${sanitizePhoneNumber(phone)}`}
                                    >
                                        {formatPhoneNumber(phone)}
                                    </a>
                                </p>
                            )}
                        </div>
                    </div>
                </div>
                <div className="nearby-facility-item__action tw:mt-5">
                    <button
                        type="button"
                        className="nearby-facility-item__button tw:inline-flex tw:items-center tw:justify-center tw:whitespace-nowrap tw:text-sm tw:font-medium tw:transition-colors tw:bg-primary tw:text-primary-foreground tw:shadow tw:hover:bg-primary/90 tw:h-9 tw:px-4 tw:py-2 tw:rounded-md tw:w-full"
                        onClick={handleViewUnits}
                        aria-label={`View available units at ${name}`}
                    >
                        See Units
                    </button>
                </div>
            </div>
        </div>
    );
};
